import { useState } from "react";
import { useDebounce } from "../../../../hooks/useDebounce";
import LimitSelect from "../../../../components/elements/LimitSelect";
import Search from "../../../../components/form/Search";
import Table from "../../../../components/elements/table/Table";
import dayjs from "dayjs";
import { lngDetect } from "../../../../helpers/lngDetect";
import {
  csvExporter,
  handleFilterChange,
  xlsxExporter,
} from "../../../../helpers";
import { tableFilter } from "../../../../helpers/tableFilterOptions";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "react-toastify";
import { useOutletContext } from "react-router-dom";
import { useOrganization } from "../../../../context/OrganizationContext";
import * as XLSX from "xlsx";
import {
  exportReports,
  orderReportList,
  saleLReportist,
} from "../../../../services/eventReportService";
import ExportDropdown from "../EventReport/ExportDropdown";
import Button from "../../../form/Button";
import DatePicker from "../../../form/DatePicker";
import Badge from "../../../elements/Badge";
import { countList, eventCountList } from "../../../../services/eventService";
import { useTranslation } from "react-i18next";
import { classNames } from "../../../../helpers/classNames";

const productTypeList = [
  { name: "Ticket", value: "ticket", type: "checkbox" },
  { name: "Addon", value: "addons-event", type: "checkbox" },
];

const getProductTypeStyle = (type) => {
  const typeLower = type?.toLowerCase();

  const typeStyles = {
    ticket: {
      backgroundColor: "#EDE9FE", // Purple background
      color: "#7C3AED", // Purple text
      borderColor: "#8B5CF6", // Purple border
    },
    "add-on": {
      backgroundColor: "#ECFDF5", // Emerald background
      color: "#059669", // Emerald text
      borderColor: "#10B981", // Emerald border
    },
  };

  return (
    typeStyles[typeLower] || {
      backgroundColor: "#F3F4F6",
      color: "#374151",
      borderColor: "#6B7280",
    }
  );
};

const SalesList = ({ selectedEvents = [], setDrawer = () => {} }) => {
  const { t } = useTranslation();
  const { event } = useOutletContext();
  const { selectedOrganization } = useOrganization();
  const [selectedItems, setSelectedItems] = useState([]);
  const [keyword, setKeyword] = useState("");
  const search = useDebounce(keyword, 300);
  const queryClient = useQueryClient();
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [filters, setFilters] = useState({
    limit: 10,
    page: 1,
    sortQuery: "-createdAt",
  });

  // Fetch orders data
  const { data, isLoading, isFetching } = useQuery({
    queryKey: [
      "sales-reports",
      filters,
      search,
      selectedEvents?.map((item) => item?._id),
    ],
    queryFn: async () => {
      const res = await saleLReportist({
        ...filters,
        ...(filters?.startDate &&
          filters?.endDate && {
            startDate: dayjs(filters?.startDate).format("YYYY-MM-DD"),
            endDate: dayjs(filters?.endDate).format("YYYY-MM-DD"),
          }),
        ...(search && { keyword: search }),
        event: selectedEvents?.map((item) => item?._id),
        orderStatus: ["confirmed"],
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.docs;
    },
    // enabled: selectedEvents?.length > 0,
  });

  // Handle export functionality with client-side download and API call
  const handleExport = async (format) => {
    setIsExportOpen(false);
    toast.info("Preparing export data...");
    try {
      const exportResponse = await saleLReportist({
        ...filters,
        ...(filters?.startDate &&
          filters?.endDate && {
            startDate: dayjs(filters?.startDate).format("YYYY-MM-DD"),
            endDate: dayjs(filters?.endDate).format("YYYY-MM-DD"),
          }),
        ...(search && { keyword: search }),
        event: selectedEvents?.map((item) => item?._id),
        orderStatus: ["confirmed"],
        limit: data?.metadata?.totalDocs, // Large number to get all records
        page: 1,
      });

      if (exportResponse?.docs?.status !== 200) {
        toast.error(exportResponse?.message || "Failed to fetch export data");
        return;
      }

      const orderData = exportResponse?.docs?.data || [];

      if (!orderData || orderData.length === 0) {
        toast.warning("No data available to export");
        return;
      }
      // Transform the order data for export
      const exportData = orderData.map((sale) => {
        const attendee_name = sale?.attendeeData
          ?.filter((attendee) =>
            ["First Name", "Last Name"].includes(attendee?.question)
          )
          ?.map((attendee) => `${attendee?.answers || ""}`.trim())
          .join(" ");
        const attendee_email = sale?.attendeeData
          ?.filter((attendee) => ["Email"].includes(attendee?.question))
          ?.map((attendee) => `${attendee?.answers || ""}`.trim())
          .join(" ");
        return {
          "Ticket Name": lngDetect(sale?.ticketInfo?.name) || "-",
          Event: lngDetect(sale?.eventData?.title) || "-",
          Type: sale?.ticketInfo?.type === "ticket" ? "Ticket" : "Add-on",
          "Booking / Full paid / free ticket":
            sale?.ticketInfo?.paymentType !== "free"
              ? sale?.ticketInfo?.fullPaid === "no" && sale?.fullPaid !== "yes"
                ? "Booking"
                : "full paid"
              : "Free",
          "Booking payment":
            sale?.ticketInfo?.fullPaid === "no"
              ? "$" + parseFloat(sale?.ticketInfo?.price || 0).toFixed(2)
              : "N/A",
          "total payment":
            "$" +
            parseFloat(
              sale?.ticketInfo?.fullPaid === "no"
                ? sale?.finalPrice
                : sale?.totalPrice || 0
            ).toFixed(2),
          "Order Date": sale?.createdAt
            ? dayjs(sale?.createdAt).format("DD MMM YYYY")
            : "-",
          Attendee: attendee_name
            ? attendee_name
            : `${sale?.userData?.firstName || ""} ${
                sale?.userData?.lastName || ""
              }`,
        };
      });

      if (format === "csv") {
        csvExporter(exportData, "sales-summary").then(async (result) => {
          if (result?.status === 200) {
            const resp = await exportReports({
              name: result?.fileName,
              file: result?.file,
              type: result?.fileCategory,
              status: "success",
              organization: selectedOrganization,
            });
            if (resp?.status !== 201) {
              toast.error(resp?.message || "Something wrong..");
            }
          }
        });
      } else if (format === "xlsx") {
        xlsxExporter(exportData, "sales-summary").then(async (result) => {
          if (result?.status === 200) {
            const resp = await exportReports({
              name: result?.fileName,
              file: result?.file,
              type: result?.fileCategory,
              status: "success",
              organization: selectedOrganization,
            });
            if (resp?.status !== 201) {
              toast.error(resp?.message || "Something wrong..");
            }
          }
        });
      }
    } catch (error) {
      console.error("Export failed:", error);
      toast.error("Export failed. Please try again.");
    } finally {
      setIsExportOpen(false);
    }
  };

  const tableHeadData = [
    {
      _id: 1,
      name: t("Ticket Name"),
      align: "left",
    },
    {
      _id: "event",
      name: t("Event"),
      align: "left",
    },
    {
      _id: 2,
      name: t("Type"),
      align: "left",
      ...tableFilter({
        filterKey: "ticketType",
        filters,
        setFilters: (val) => {
          setFilters((pre) => ({ ...pre, ...val, page: 1 }));
        },
        filterList: [...productTypeList],
      }),
    },
    {
      _id: "Booking/Fullpaid/freeticket",
      name: t("Booking / Full paid / free ticket"),
      align: "left",
    },
    {
      _id: "deposite",
      name: t("Booking payment"),
      align: "left",
    },
    {
      _id: "4",
      name: t("total payment"),
      align: "left",
    },
    {
      _id: 5,
      name: t("Order Date"),
      align: "left",
    },
    {
      _id: 6,
      name: t("Attendee"),
      align: "left",
    },
  ];
  // Table Body Data
  const tableData = data?.data?.map((item) => {
    // Product type styling
    const productType = item?.ticketInfo?.type || "ticket";
    const productTypeStyle = getProductTypeStyle(productType);
    const attendee_name = item?.attendeeData
      ?.filter((attendee) =>
        ["First Name", "Last Name"].includes(attendee?.question)
      )
      ?.map((attendee) => `${attendee?.answers || ""}`.trim())
      .join(" ");
    const attendee_email = item?.attendeeData
      ?.filter((attendee) => ["Email"].includes(attendee?.question))
      ?.map((attendee) => `${attendee?.answers || ""}`.trim())
      .join(" ");

    return {
      _id: item?._id,
      rowData: [
        {
          _id: 2,
          type: "text",
          align: "left",
          cellClass: "!min-w-[150px] font-medium",
          data: lngDetect(item?.ticketInfo?.name) || "-",
        },
        {
          _id: "eventData",
          type: "text",
          align: "left",
          cellClass: "!min-w-[150px] font-medium",
          data: lngDetect(item?.eventData?.title) || "-",
        },
        {
          _id: 3,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <span
              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium border"
              style={{
                backgroundColor: productTypeStyle.backgroundColor,
                color: productTypeStyle.color,
                borderColor: productTypeStyle.borderColor,
              }}
            >
              {item?.ticketInfo?.type === "ticket" ? "Ticket" : "Add-on"}
            </span>
          ),
        },
        {
          _id: 4,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] text-purple-600",
          data:
            item?.ticketInfo?.paymentType !== "free" ? (
              <Badge
                type={
                  item?.ticketInfo?.fullPaid !== "no" ||
                  item?.fullPaid === "yes"
                    ? "danger"
                    : "warning"
                }
                className={"rounded-full py-1 text-xs font-medium"}
                label={
                  item?.ticketInfo?.fullPaid === "no" &&
                  item?.fullPaid !== "yes"
                    ? "booking"
                    : "full paid"
                }
              />
            ) : (
              <Badge
                type={"info"}
                className={"rounded-full py-1 text-xs font-medium"}
                label={"free"}
              />
            ),
        },
        {
          _id: "deposite",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] text-purple-600",
          data:
            item?.ticketInfo?.fullPaid === "no" ? (
              `${item?.eventData?.currency?.symbol || "$"} ${parseFloat(
                item?.ticketInfo?.price || 0
              ).toFixed(2)}`
            ) : (
              <span className="text-gray-300">N/A</span>
            ),
        },
        {
          _id: 5,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] text-purple-600",
          data: `${item?.eventData?.currency?.symbol || "$"} ${parseFloat(
            item?.ticketInfo?.fullPaid === "no"
              ? item?.finalPrice
              : item?.totalPrice || 0
          ).toFixed(2)}`,
        },
        {
          _id: 6,
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px] text-gray-600",
          data: item?.createdAt
            ? dayjs(item?.createdAt).format("DD MMM YYYY")
            : "-",
        },
        {
          _id: "customer",
          type: "text",
          align: "left",
          cellClass: "!min-w-[100px]",
          data: (
            <div>
              <div className="flex items-center gap-1">
                {attendee_name ? (
                  attendee_name
                ) : (
                  <>
                    <span>{item?.userData?.firstName || ""}</span>
                    <span>{item?.userData?.lastName || ""}</span>
                  </>
                )}
              </div>
              <div className="text-xs text-gray-400 flex items-center gap-1">
                <i className="fa-fw fa-regular fa-envelope"></i>
                {attendee_email || item?.userData?.email}
              </div>
            </div>
          ),
        },
      ],
    };
  });

  const { data: eventCount, isLoading: eventCountLoading } = useQuery({
    queryKey: [
      "event-count-data",
      selectedEvents?.map((item) => item?._id),
      filters?.startDate &&
        filters?.endDate && {
          startDate: dayjs(filters?.startDate).format("YYYY-MM-DD"),
          endDate: dayjs(filters?.endDate).format("YYYY-MM-DD"),
        },
    ],
    queryFn: async () => {
      const res = await countList({
        ...(filters?.startDate &&
          filters?.endDate && {
            startDate: dayjs(filters?.startDate).format("YYYY-MM-DD"),
            endDate: dayjs(filters?.endDate).format("YYYY-MM-DD"),
          }),
        event: selectedEvents?.map((item) => item?._id),
        orderStatus: ["confirmed"],
        sortQuery: "-createdAt",
      });
      if (res?.status !== 200) {
        toast.warning(res?.message);
      }
      return res?.data;
    },
  });

  const statistics = [
    { label: t("Ticket Sold"), value: eventCount?.ticketsSold || 0 },
    { label: t("Orders"), value: eventCount?.totalOrder || 0 },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-5 absolute -top-28 right-6">
        {/* <div className="bg-white border border-gray-200 rounded-lg p-6">
              <h3 className="text-gray-600 text-sm font-medium mb-2">
                {t("Net Sales")}
              </h3>
              <div className="text-3xl font-bold text-gray-900">
                $ {eventCount?.netSales || 0}
              </div>
            </div> */}

        {statistics?.map((s, i) => (
          <div
            key={i?.toString()}
            className={classNames(
              "relative pl-4",
              "before:content-[''] before:w-[6px] before:h-full before:bg-green-300 before:rounded-full before:absolute before:top-0 before:left-0"
            )}
          >
            <div className="text-sm text-gray-400 font-medium">{s?.label}</div>
            <div className="text-lg font-semibold text-deep-purple">
              {s?.value}
            </div>
          </div>
        ))}
      </div>
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-36">
              <LimitSelect
                value={filters?.limit}
                allValue={data?.metadata?.totalDocs}
                onChange={(val) => handleFilterChange("limit", val, setFilters)}
              />
            </div>
            <div className="w-72">
              <Search
                placeholder={t("Search here")}
                divClasses={"mb-0"}
                search={(val) => {
                  setKeyword(val);
                  handleFilterChange("page", 1, setFilters);
                }}
              />
            </div>
            <div className="w-60">
              <DatePicker
                dateFormat="DD MMM YYYY"
                range
                onChange={(val) => {
                  if (val?.startDate && val?.endDate) {
                    setFilters((pre) => ({ ...pre, ...val }));
                  } else {
                    setFilters((pre) => {
                      delete pre?.startDate;
                      delete pre?.endDate;
                      return { ...pre };
                    });
                  }
                }}
                inputClassName="rounded-full h-12 px-4"
              />
            </div>
          </div>
          <div className="flex gap-4 items-center">
            <Button
              buttonIcon={"fa-light fa-calendar-days"}
              buttonLabel={`${t("Select Events")} (${
                selectedEvents?.length || 0
              })`}
              buttonFunction={() => {
                setDrawer({ isOpen: true, data: null });
              }}
            />
            <ExportDropdown
              onExport={handleExport}
              isOpen={isExportOpen}
              onToggle={() => setIsExportOpen(!isExportOpen)}
            />
          </div>
        </div>

        {/* Orders Table */}
        <Table
          tableHeadData={tableHeadData}
          tableData={tableData}
          isLoder={isLoading}
          pageCount={data?.metadata?.totalPages}
          currentPage={filters?.page}
          onPageChange={(val) => {
            handleFilterChange("page", val, setFilters);
            setSelectedItems([]);
          }}
          originalData={[...(data?.data || [])]}
          setOriginalData={(val) => {
            queryClient.setQueryData(
              ["order", filters, search, selectedOrganization],
              (state) => {
                if (state) {
                  return { ...state, data: val };
                }
              }
            );
          }}
          // handleOrdering={(newData, oldData) =>
          //   handleOrdering({ newData, oldData })
          // }
          isFetching={isFetching}
          selectedItems={selectedItems}
          setSelectedItems={setSelectedItems}
          tableActionDropdownContainer={"!w-48"}
        />
      </div>
    </div>
  );
};

export default SalesList;
